import type { ConferenceTag } from '@trinity/chat/types';
import { ConferenceTagType } from '@trinity/chat/enums';

export const TAG_COLORS = [
  { highlight: '#CCECF9', menu: '#B4DFF1', hover: '#9CD3EA' },
  { highlight: '#DCDCF8', menu: '#C8C9F4', hover: '#ABACEB' },
  { highlight: '#F7C0CD', menu: '#EEAABA', hover: '#EF819C' },
  { highlight: '#E4F1CF', menu: '#D6E8B8', hover: '#C1DD93' },
  { highlight: '#C6F5F5', menu: '#AEF3F3', hover: '#8DECEC' },
  { highlight: '#F7DAD4', menu: '#EFB5AA', hover: '#E7917F' },
];

export const DEFAULT_TAG_COLOR = TAG_COLORS[0].menu;

export function assertIsGoodQuoteTag(tag: Pick<ConferenceTag, 'name' | 'typeId'>): boolean {
  return tag.typeId === ConferenceTagType.Global && tag.name === 'Good Quote';
}

export function computeTranscriptColor({
  color,
  opacity,
  darkenBy = 0.3,
}: {
  color: string;
  opacity: number;
  darkenBy?: number;
}): string {
  const [h, s, l] = hexToHsl(color);
  const newL = Math.max(0, Math.min(1, l * (1 - darkenBy)));
  const newS = Math.max(0, Math.min(1, s * 1.2));
  const [r, g, b] = hslToRgb(h, newS, newL);
  return `rgb(${r}, ${g}, ${b}, ${opacity}%)`;
}

function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    default:
      h = ((r - g) / d + 4) / 6;
  }
  return [h * 360, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const c = Math.round(l * 255);
    return [c, c, c];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue2rgb = (t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const hn = h / 360;
  return [
    Math.round(hue2rgb(hn + 1 / 3) * 255),
    Math.round(hue2rgb(hn) * 255),
    Math.round(hue2rgb(hn - 1 / 3) * 255),
  ];
}
